import React from 'react';
import { useParams } from 'react-router-dom';
import { Box, Heading, Text, VStack } from '@chakra-ui/react';
import { useCombinedStore } from '../../store/hooks/useCombinedStore';
import {
    Product,
    Order,
    Flight,
    Bus,
    Car,
    Accommodation,
} from '../../types/data-interface';
import { useCustomTheme } from '../../contexts/CustomThemeContext';

const SearchDetailsPage: React.FC = () => {
    const { type, id } = useParams<{ type: string; id: string; }>();

    const {
        products,
        orders,
        flights,
        buses,
        cars,
        accommodations,
    } = useCombinedStore();

    const { theme, themeName, toggleTheme } = useCustomTheme();

    /**
     * ⚠️ IMPORTANT:
     * We return `unknown | null` on purpose.
     * This avoids conflicts between duplicate domain models
     * (e.g. Order from API vs Order from data-interface).
     */
    const getItem = (): unknown | null => {
        switch (type) {
            case 'products':
                return products.find(item => item._id === id) ?? null;

            case 'orders':
                return orders.find(item => item._id === id) ?? null;

            case 'flights':
                return flights.find(item => item._id === id) ?? null;

            case 'buses':
                return buses.find(item => item._id === id) ?? null;

            case 'cars':
                return cars.find(item => item._id === id) ?? null;

            case 'accommodations':
                return accommodations.find(item => item._id === id) ?? null;

            default:
                return null;
        }
    };

    const item = getItem();

    if (!type || !id) {
        return <Text p={4}>Invalid search parameters.</Text>;
    }

    if (!item) {
        return (
            <Box p={4}>
                <Text>No details found for ID: {id}</Text>
            </Box>
        );
    }

    return (
        <Box p={6} bg={theme.background} color={theme.text}>
            <Heading mb={4}>Details for {type}</Heading>

            <VStack spacing={3} align="start">
                {/* PRODUCTS */}
                {type === 'products' && (() => {
                    const product = item as Product;
                    return (
                        <>
                            <Text><strong>Title:</strong> {product.title}</Text>
                            <Text><strong>Price:</strong> ${product.price}</Text>
                            <Text><strong>Description:</strong> {product.description}</Text>
                        </>
                    );
                })()}

                {/* ORDERS */}
                {type === 'orders' && (() => {
                    const order = item as Order;
                    return (
                        <>
                            <Text><strong>Order ID:</strong> {order._id}</Text>

                            {order.fromAddress && (
                                <Text><strong>From:</strong> {order.fromAddress}</Text>
                            )}

                            {order.toAddress && (
                                <Text><strong>To:</strong> {order.toAddress}</Text>
                            )}

                            <Text><strong>Total:</strong> ${order.total}</Text>
                            <Text><strong>Status:</strong> {order.status}</Text>
                        </>
                    );
                })()}

                {/* FLIGHTS */}
                {type === 'flights' && (() => {
                    const flight = item as Flight;
                    return (
                        <>
                            <Text><strong>Airline:</strong> {flight.airline}</Text>
                            <Text><strong>From:</strong> {flight.from}</Text>
                            <Text><strong>To:</strong> {flight.to}</Text>
                            <Text><strong>Departure:</strong> {flight.departureDate}</Text>
                            <Text><strong>Arrival:</strong> {flight.returnDate}</Text>
                        </>
                    );
                })()}

                {/* BUSES */}
                {type === 'buses' && (() => {
                    const bus = item as Bus;
                    return (
                        <>
                            <Text><strong>Company:</strong> {bus.company}</Text>
                            <Text><strong>From:</strong> {bus.from}</Text>
                            <Text><strong>To:</strong> {bus.to}</Text>
                            <Text><strong>Departure:</strong> {bus.departureDate}</Text>
                        </>
                    );
                })()}

                {/* CARS */}
                {type === 'cars' && (() => {
                    const car = item as Car;
                    return (
                        <>
                            <Text><strong>Brand:</strong> {car.brand}</Text>
                            <Text><strong>Model:</strong> {car.model}</Text>
                            <Text><strong>Make:</strong> {car.make}</Text>
                            <Text><strong>Year:</strong> {car.year}</Text>
                        </>
                    );
                })()}

                {/* ACCOMMODATIONS */}
                {type === 'accommodations' && (() => {
                    const acc = item as Accommodation;
                    return (
                        <>
                            <Text><strong>Name:</strong> {acc.name}</Text>
                            <Text><strong>Location:</strong> {acc.location}</Text>
                            <Text><strong>Type:</strong> {acc.roomType}</Text>
                            <Text><strong>Price Per Night:</strong> ${acc.price}</Text>
                        </>
                    );
                })()}
            </VStack>
        </Box>
    );
};

export default SearchDetailsPage;
